import type { IntlShape } from 'react-intl';
import { localDay } from '../planMode';

/**
 * Times of the rollout plan in the artist's time zone (owner decision 7). The plan answer names
 * the zone (`timeZone`); with none, the times show in the viewer's zone.
 */

/** The browser time zone (IANA), or undefined when the runtime does not know it. */
export function browserTimeZone(): string | undefined {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return typeof zone === 'string' && zone !== '' ? zone : undefined;
  } catch {
    return undefined;
  }
}

/** "18:00" of an ISO time in the zone. */
export function hourInZone(intl: IntlShape, iso: string, timeZone: string | null): string {
  return intl.formatTime(new Date(iso), { hour: '2-digit', minute: '2-digit', timeZone: timeZone ?? undefined });
}

/** "Fri 4 Jun, 18:00" of an ISO time in the zone. */
export function dayAndHourInZone(intl: IntlShape, iso: string, timeZone: string | null): string {
  const at = new Date(iso);
  const zone = timeZone ?? undefined;
  const day = intl.formatDate(at, { weekday: 'short', day: 'numeric', month: 'short', timeZone: zone });
  return `${day}, ${intl.formatTime(at, { hour: '2-digit', minute: '2-digit', timeZone: zone })}`;
}

/** "Tallinn" of `Europe/Tallinn`: the last part, with spaces. */
export function zoneCity(timeZone: string | null): string | null {
  if (timeZone === null) {
    return null;
  }
  const last = timeZone.split('/').pop() ?? timeZone;
  return last.replace(/_/g, ' ');
}

function parts(ms: number, timeZone: string): Record<string, number> {
  const out: Record<string, number> = {};
  new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
    .formatToParts(new Date(ms))
    .forEach((part) => {
      if (part.type !== 'literal') {
        out[part.type] = Number(part.value);
      }
    });
  return out;
}

function offsetMs(ms: number, timeZone: string): number {
  const p = parts(ms, timeZone);
  const asUtc = Date.UTC(p.year ?? 1970, (p.month ?? 1) - 1, p.day ?? 1, p.hour ?? 0, p.minute ?? 0, p.second ?? 0);
  return asUtc - Math.floor(ms / 1000) * 1000;
}

const pad = (value: number | undefined): string => String(value ?? 0).padStart(2, '0');

/** The `datetime-local` value (`YYYY-MM-DDTHH:mm`) of an ISO time in the zone (the viewer's zone with none). */
export function wallTimeOf(iso: string, timeZone: string | null): string {
  const ms = Date.parse(iso);
  if (timeZone === null) {
    const at = new Date(ms);
    return `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}T${pad(at.getHours())}:${pad(
      at.getMinutes()
    )}`;
  }
  const p = parts(ms, timeZone);
  return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`;
}

/**
 * `YYYY-MM-DD` of a moment in the zone: "today" of the artist, as the server counts it. With no
 * zone, or a zone that the runtime does not know, it is the viewer's own day.
 */
export function dayInZone(now: Date, timeZone: string | null | undefined): string {
  if (timeZone === null || timeZone === undefined || timeZone === '') {
    return localDay(now);
  }
  try {
    return wallTimeOf(now.toISOString(), timeZone).slice(0, 10);
  } catch {
    return localDay(now);
  }
}

/** The ISO time of a `datetime-local` value in the zone, or null for a bad value. */
export function isoOfWallTime(wall: string, timeZone: string | null): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(wall);
  if (match === null) {
    return null;
  }
  const [, y, mo, d, h, mi] = match.map(Number);
  if (timeZone === null) {
    return new Date(y ?? 0, (mo ?? 1) - 1, d, h, mi).toISOString();
  }
  const guess = Date.UTC(y ?? 0, (mo ?? 1) - 1, d, h, mi);
  const first = guess - offsetMs(guess, timeZone);
  return new Date(guess - offsetMs(first, timeZone)).toISOString();
}
